import RichText from "@/components/common/richText/richText.component";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Link from "next/link";
import type { ReactNode } from "react";
import type { MarketingCta } from "../marketing.type";

interface PageHeroProps {
  title: string;
  lead?: string;
  // Extra intro paragraphs under the lead (`**bold**` supported).
  paragraphs?: string[];
  ctas: MarketingCta[];
  // "default": big two-column hero. "tight": smaller headline, still two
  // columns. "lite": tight headline, single column - for pages whose first
  // section is the interactive demo.
  variant?: "default" | "tight" | "lite";
  visual?: ReactNode;
}

const PageHero = ({
  title,
  lead,
  paragraphs = [],
  ctas,
  variant = "default",
  visual,
}: PageHeroProps) => {
  const isLite = variant === "lite";
  return (
    <section
      aria-labelledby="hero-title"
      // Dark like the landing-page heroes (bg-secondary-2), so 2.0 pages
      // match the rest of the site - and the header blends into it until
      // scroll (see heroBackgrounds in src/lib/backgrounds.ts).
      className={cn(
        "bg-secondary-2 pt-10 pb-16 text-white",
        isLite ? "lg:pt-12 lg:pb-[72px]" : "lg:pt-[72px] lg:pb-24",
      )}
    >
      <div
        className={cn(
          "site-wrap grid items-center gap-9",
          !isLite && "lg:grid-cols-[1.05fr_0.95fr] lg:gap-14",
        )}
      >
        <div>
          <h1
            id="hero-title"
            className={cn(
              "leading-[1.02] font-extrabold tracking-[-0.035em] text-white",
              variant === "default"
                ? "max-w-[13ch] text-[clamp(2.6rem,5.6vw,4.6rem)]"
                : "text-[clamp(2.3rem,4.8vw,3.9rem)]",
              variant === "tight" && "max-w-[16ch]",
              isLite && "max-w-[18ch]",
            )}
          >
            {title}
          </h1>
          {lead && (
            <p
              className={cn(
                "mt-6 text-[clamp(1.05rem,1.6vw,1.25rem)] leading-relaxed text-white/80",
                isLite ? "max-w-[60ch]" : "max-w-[50ch]",
              )}
            >
              {lead}
            </p>
          )}
          {paragraphs.map((paragraph) => (
            <p
              key={paragraph}
              className={cn(
                "mt-4 text-base leading-relaxed text-white/75",
                isLite ? "max-w-[64ch]" : "max-w-[52ch]",
              )}
            >
              <RichText text={paragraph} strongClassName="font-semibold text-white" />
            </p>
          ))}
          {ctas.length > 0 && (
            <div className="mt-9 flex flex-wrap gap-3">
              {ctas.map((cta) => (
                <Button
                  key={cta.route}
                  asChild
                  // On the dark hero the outline CTA is drawn in white.
                  variant={cta.variant === "brandOutline" ? "whiteOutline" : "brand"}
                  size="pill"
                  className="w-full sm:w-auto"
                >
                  <Link href={cta.route}>{cta.label}</Link>
                </Button>
              ))}
            </div>
          )}
        </div>
        {visual && <div className="relative min-w-0">{visual}</div>}
      </div>
    </section>
  );
};

export default PageHero;
